/**
 * useChatStream — Standard chatbot history hook for PLR/CLR quoting.
 *
 * Design principle: ONE code path — always connect to /chat-stream.
 * The server replays all saved events from position 0 on every connection,
 * so page-reopen, reconnect after a network drop, and first-load all work
 * identically. No separate /chat-events fetch, no isFinished branch.
 *
 * Reconnect strategy:
 *   - Stream closes without run_complete  → reconnect with exponential backoff
 *   - No event received for 30s (idle)   → reconnect (connection is likely dead)
 *   - Backoff: 1s → 2s → 4s → 8s → 16s → 30s max, resets on success
 *   - All reconnects dedup via msgKey — no duplicate messages shown
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const MYAMP = process.env.REACT_APP_MyAMP_Url;
const DEFAULT_BASE = `${MYAMP}/api/ams/rate-quoting`;

const IDLE_TIMEOUT_MS  = 30_000; // 30s without any event → reconnect
const IDLE_CHECK_MS    = 5_000;  // check every 5s

// ── Message type ──────────────────────────────────────────────────────────────

export interface EmailDraft {
  subject: string;
  body: string;
  insured_name?: string;
  lob?: string;
  to_email?: string;
}

export interface ChatMessage {
  role: 'assistant' | 'user' | 'tool' | 'system';
  content: string;
  ts: string;
  tool?: string;
  status?: 'running' | 'done' | 'error';
  input_request?: boolean;
  fields?: string;
  step_id?: string;
  summary?: string;
  quotes_count?: number;
  email_draft?: EmailDraft;
}

/** @deprecated Use ChatMessage */
export type ChatEvent = ChatMessage;

export type StreamStatus = 'idle' | 'connecting' | 'streaming' | 'complete' | 'error';

// ── Dedup ─────────────────────────────────────────────────────────────────────

function msgKey(m: ChatMessage): string {
  const body = m.content || m.tool || '';
  if (m.role === 'user') return `user:${body}`;
  if (m.role === 'tool') return `tool:${m.tool}:${m.status}:${m.ts || ''}`;
  return `${m.role}:${body}:${m.ts || ''}`;
}

function addUnique(prev: ChatMessage[], msg: ChatMessage): ChatMessage[] {
  const key = msgKey(msg);
  return prev.some(e => msgKey(e) === key) ? prev : [...prev, msg];
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useChatStream(runId: string | null, _isFinished?: boolean, basePath?: string) {
  const BASE = basePath ? `${MYAMP}/api/ams/${basePath}` : DEFAULT_BASE;

  const [events, setEvents]     = useState<ChatMessage[]>([]);
  const [status, setStatus]     = useState<StreamStatus>('idle');
  const [thinking, setThinking] = useState(false);

  // Increment to force a new connection on the same runId.
  const [reconnectKey, setReconnectKey] = useState(0);

  // "runId:reconnectKey" — prevents React StrictMode double-mount from opening
  // two connections while still letting reconnectKey increments open new ones.
  const loadedKeyRef      = useRef<string | null>(null);
  const loadedRunIdRef    = useRef<string | null>(null); // track when runId changes
  const lastEventAtRef    = useRef<number>(0);           // epoch of last received event
  const reconnectDelayRef = useRef<number>(1_000);       // current backoff ms

  // ── Idle watchdog ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (status !== 'streaming') return;
    const id = window.setInterval(() => {
      if (lastEventAtRef.current > 0 && Date.now() - lastEventAtRef.current > IDLE_TIMEOUT_MS) {
        // Connection appears dead — unlock and reconnect
        loadedKeyRef.current = null;
        setReconnectKey(k => k + 1);
      }
    }, IDLE_CHECK_MS);
    return () => window.clearInterval(id);
  }, [status]);

  // ── Instant reconnect on tab focus ─────────────────────────────────────────
  useEffect(() => {
    if (status !== 'streaming') return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        // Force a fresh SSE connection — the server replays all history,
        // so this is safe and deduped by msgKey.
        loadedKeyRef.current = null;
        setReconnectKey(k => k + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [status]);

  // ── SSE connection ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!runId) return;

    const key = `${runId}:${reconnectKey}`;
    if (loadedKeyRef.current === key) return; // StrictMode guard
    loadedKeyRef.current = key;

    // Only clear events when switching to a different run
    if (loadedRunIdRef.current !== runId) {
      setEvents([]);
      loadedRunIdRef.current = runId;
    }

    setStatus('connecting');
    lastEventAtRef.current = Date.now();

    const controller = new AbortController();
    const authToken  = localStorage.getItem('authToken') || '';

    function scheduleReconnect() {
      const delay = reconnectDelayRef.current;
      reconnectDelayRef.current = Math.min(delay * 2, 30_000);
      setTimeout(() => {
        if (!controller.signal.aborted) {
          loadedKeyRef.current = null;
          setReconnectKey(k => k + 1);
        }
      }, delay);
    }

    (async () => {
      try {
        // Single connection path: /chat-stream replays all history from index 0,
        // then streams live events. Works for first-load, reopen, and reconnect.
        const res = await fetch(`${BASE}/runs/${runId}/chat-stream`, {
          headers: { Authorization: `Bearer ${authToken}` },
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;
        if (!res.ok || !res.body) {
          setStatus('error');
          scheduleReconnect();
          return;
        }

        setStatus('streaming');
        reconnectDelayRef.current = 1_000; // reset on successful connect

        const reader  = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer    = '';

        try {
          while (!controller.signal.aborted) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split('\n\n');
            buffer = parts.pop() ?? '';

            for (const part of parts) {
              if (controller.signal.aborted) return;
              const dataLine = part.split('\n').find(l => l.startsWith('data:'));
              if (!dataLine) continue; // SSE comment / keepalive — ignored by design
              try {
                const msg = JSON.parse(dataLine.replace(/^data:\s*/, '')) as ChatMessage;
                lastEventAtRef.current = Date.now();
                setEvents(prev => addUnique(prev, msg));

                if (msg.role === 'system' && msg.content === 'run_complete') {
                  setStatus('complete');
                  reconnectDelayRef.current = 1_000;
                  return; // clean exit — run is done, no reconnect
                }
              } catch {
                // ignore malformed frame
              }
            }
          }
        } catch (err: any) {
          // AbortError is expected on unmount (controller.abort()) — not a real error.
          // Catch it here before it escapes the finally and surfaces in React Dev overlay.
          if (controller.signal.aborted || err?.name === 'AbortError') return;
          throw err; // re-throw genuine errors to the outer catch
        } finally {
          // reader.cancel() returns a Promise. Use .catch() to suppress the rejection
          // that occurs when the stream is already aborted — try/catch only catches
          // synchronous throws, not async Promise rejections.
          reader.cancel().catch(() => {});
        }

        // Stream closed before run_complete — network drop or server restart.
        if (!controller.signal.aborted) {
          setStatus('connecting');
          scheduleReconnect();
        }
      } catch (err: any) {
        if (controller.signal.aborted || (err as any)?.name === 'AbortError') return;
        setStatus('error');
        scheduleReconnect();
      }
    })();

    return () => {
      controller.abort();
      loadedKeyRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, reconnectKey]);

  // ── Post-quote Q&A ────────────────────────────────────────────────────────
  const sendMessage = useCallback(async (text: string) => {
    if (!runId) return;
    const authToken = localStorage.getItem('authToken') || '';

    const userMsg: ChatMessage = { role: 'user', content: text, ts: new Date().toISOString() };
    setEvents(prev => addUnique(prev, userMsg));
    setThinking(true);

    try {
      const res = await fetch(`${BASE}/runs/${runId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ message: text, history: [] }),
      });

      const contentType = res.headers.get('content-type') || '';
      let reply: string;
      if (contentType.includes('application/json')) {
        const data = await res.json();
        reply = data.reply || data.errorMessage || 'No response';
      } else {
        reply = (await res.text()).trim() || 'No response';
      }

      setEvents(prev => addUnique(prev, {
        role: 'assistant',
        content: reply,
        ts: new Date().toISOString(),
      }));
    } catch (err: any) {
      setEvents(prev => [...prev, {
        role: 'assistant',
        content: `Error: ${err?.message || 'Failed to get response'}`,
        ts: new Date().toISOString(),
      }]);
    } finally {
      setThinking(false);
    }
  }, [runId, BASE]);

  // Explicit event load (backward compat for any callers that use this)
  const loadSavedEvents = useCallback(() => {
    // No-op — the SSE stream handles replay automatically on every connection.
    // Reconnect triggers a fresh SSE which replays all history from the server.
    loadedKeyRef.current = null;
    setReconnectKey(k => k + 1);
  }, []);

  // Add user message locally (instant feedback before SSE delivers server echo)
  const addUserInput = useCallback((text: string) => {
    setEvents(prev => addUnique(prev, {
      role: 'user',
      content: text,
      ts: new Date().toISOString(),
    }));
  }, []);

  return { events, status, thinking, sendMessage, loadSavedEvents, addUserInput };
}
